/**
 * Turning a subject into something a person can recognise.
 *
 * Subjects are random (`anon_x7K…`) and names are not unique — two people can
 * both be "Ada". In a live room you need to tell them apart at a glance, while
 * someone is talking, on a phone. Colour does that faster than reading.
 *
 * The hue is derived from the subject, so it is stable across reconnects and
 * identical on every device in the room: everyone sees the same Ada in the same
 * teal. Deriving from the NAME instead would collapse the two Adas together,
 * which is exactly the confusion this is meant to prevent.
 */

/** FNV-1a. Small, fast, and stable across engines — which matters more than distribution here. */
function hash(value: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * A hue for this subject.
 *
 * The golden-angle step spreads consecutive hashes far apart on the wheel, so
 * two people who join together rarely land on neighbouring colours.
 */
export function hueFor(sub: string): number {
  return Math.round((hash(sub) * 137.508) % 360);
}

/**
 * Avatar colours, as CSS.
 *
 * Lightness and chroma are fixed and only the hue varies, so every avatar sits
 * at the same visual weight — one loud tile among quiet ones would read as a
 * status rather than as a person. Text lightness is pinned dark enough to hold
 * contrast against its own tint at every hue.
 */
export function avatarStyle(sub: string): Record<string, string> {
  const hue = hueFor(sub);
  return {
    '--avatar-bg': `oklch(0.92 0.055 ${hue})`,
    '--avatar-ink': `oklch(0.42 0.11 ${hue})`,
    '--avatar-ring': `oklch(0.68 0.15 ${hue})`,
  };
}

/** Up to two initials, from a name that may be one word, empty, or emoji. */
export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return [...parts[0]!].slice(0, 2).join('').toUpperCase();
  return (([...parts[0]!][0] ?? '') + ([...parts.at(-1)!][0] ?? '')).toUpperCase();
}
